<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import authService from '../services/auth.service'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Form fields - Personal Information
const firstName = ref('')
const lastName = ref('')
const email = ref('')

// Form fields - Password
const currentPassword = ref('')
const newPassword = ref('')
const newPasswordConfirm = ref('')

// Form fields - Notifications
const notificationPushEnabled = ref(false)
const notificationEmailEnabled = ref(false)

// Form fields - Language
const language = ref('pl')

const loading = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Get user UUID
const userUuid = computed(() => authStore.user?.uuid || '')

// Load user data from store (in-memory, always fresh after init)
const loadUserData = () => {
  const user = authStore.user
  if (user) {
    firstName.value = user.first_name || ''
    lastName.value = user.last_name || ''
    email.value = user.email || ''
    notificationPushEnabled.value = user.notification_push_enabled || false
    notificationEmailEnabled.value = user.notification_email_enabled || false
    language.value = user.language || 'pl'
  }
}

// Handle form submission
const handleSubmit = async () => {
  if (!userUuid.value) {
    error.value = t('editProfile.errorNoUserUuid')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const data: any = {
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      email: email.value.trim(),
      notification_push_enabled: notificationPushEnabled.value,
      notification_email_enabled: notificationEmailEnabled.value,
      language: language.value
    }

    // Add password fields only if user wants to change password
    if (currentPassword.value || newPassword.value || newPasswordConfirm.value) {
      data.current_password = currentPassword.value
      data.new_password = newPassword.value
      data.new_password_confirmation = newPasswordConfirm.value
    }

    await authService.updateProfile(userUuid.value, data)

    // Refresh init data after profile update
    await authStore.initUserData(userUuid.value)

    // Clear password fields
    currentPassword.value = ''
    newPassword.value = ''
    newPasswordConfirm.value = ''

    // Reload user data
    loadUserData()

    toastStore.success(t('editProfile.profileUpdated'))
  } catch (err: any) {
    console.error('Update profile error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      error.value = err.response?.data?.message || t('editProfile.errorUpdatingProfile')
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push('/categories')
}

// Load data on mount
onMounted(() => {
  loadUserData()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-2xl mx-auto px-4 py-4">
      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('editProfile.title') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('editProfile.subtitle') }}</p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Edit Form -->
      <form @submit.prevent="handleSubmit" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
        <!-- Personal Information Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('editProfile.personalInformation') }}</h2>

          <!-- Imię -->
          <div class="mb-3">
            <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.firstName') }} <span class="text-red-600">{{ t('common.required') }}</span>
            </label>
            <input
              v-model="firstName"
              type="text"
              id="first_name"
              required
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('first_name') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.firstNamePlaceholder')"
            >
            <p v-if="getFieldError('first_name')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('first_name') }}
            </p>
          </div>

          <!-- Nazwisko -->
          <div class="mb-3">
            <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.lastName') }} <span class="text-red-600">{{ t('common.required') }}</span>
            </label>
            <input
              v-model="lastName"
              type="text"
              id="last_name"
              required
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('last_name') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.lastNamePlaceholder')"
            >
            <p v-if="getFieldError('last_name')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('last_name') }}
            </p>
          </div>

          <!-- Email -->
          <div class="mb-3">
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.email') }} <span class="text-red-600">{{ t('common.required') }}</span>
            </label>
            <input
              v-model="email"
              type="email"
              id="email"
              required
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('email') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.emailPlaceholder')"
            >
            <p class="text-xs text-gray-500 mt-1">{{ t('editProfile.emailHint') }}</p>
            <p v-if="getFieldError('email')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('email') }}
            </p>
          </div>
        </div>

        <!-- Notifications Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('editProfile.notifications') }}</h2>

          <!-- Push Notifications -->
          <div class="mb-3">
            <label class="flex items-center cursor-pointer">
              <input
                v-model="notificationPushEnabled"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              >
              <span class="ml-2 text-sm text-gray-700">{{ t('editProfile.notificationPushEnabled') }}</span>
            </label>
          </div>

          <!-- Email Notifications -->
          <div class="mb-3">
            <label class="flex items-center cursor-pointer">
              <input
                v-model="notificationEmailEnabled"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              >
              <span class="ml-2 text-sm text-gray-700">{{ t('editProfile.notificationEmailEnabled') }}</span>
            </label>
          </div>
        </div>

        <!-- Language Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('editProfile.language') }}</h2>

          <div class="mb-3">
            <label for="language" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.selectLanguage') }}
            </label>
            <select
              v-model="language"
              id="language"
              class="w-full px-3 py-2 border-2 border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600 bg-white"
            >
              <option value="pl">🇵🇱 Polski</option>
              <option value="en">🇬🇧 English</option>
              <option value="fr">🇫🇷 Français</option>
              <option value="es">🇪🇸 Español</option>
              <option value="de">🇩🇪 Deutsch</option>
              <option value="it">🇮🇹 Italiano</option>
              <option value="pt">🇵🇹 Português</option>
              <option value="cs">🇨🇿 Čeština</option>
              <option value="nl">🇳🇱 Nederlands</option>
              <option value="sv">🇸🇪 Svenska</option>
              <option value="da">🇩🇰 Dansk</option>
              <option value="hu">🇭🇺 Magyar</option>
              <option value="ro">🇷🇴 Română</option>
              <option value="no">🇳🇴 Norsk</option>
              <option value="lt">🇱🇹 Lietuvių</option>
              <option value="hr">🇭🇷 Hrvatski</option>
              <option value="fi">🇫🇮 Suomi</option>
            </select>
          </div>
        </div>

        <!-- Password Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">{{ t('editProfile.changePassword') }}</h2>
          <p class="text-xs text-gray-600 mb-3">{{ t('editProfile.changePasswordHint') }}</p>

          <!-- Aktualne hasło -->
          <div class="mb-3">
            <label for="current_password" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.currentPassword') }}
            </label>
            <input
              v-model="currentPassword"
              type="password"
              id="current_password"
              autocomplete="off"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('current_password') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.currentPasswordPlaceholder')"
            >
            <p v-if="getFieldError('current_password')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('current_password') }}
            </p>
          </div>

          <!-- Nowe hasło -->
          <div class="mb-3">
            <label for="new_password" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.newPassword') }}
            </label>
            <input
              v-model="newPassword"
              type="password"
              id="new_password"
              autocomplete="new-password"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('new_password') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.newPasswordPlaceholder')"
            >
            <p class="text-xs text-gray-500 mt-1">{{ t('editProfile.newPasswordHint') }}</p>
            <p v-if="getFieldError('new_password')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('new_password') }}
            </p>
          </div>

          <!-- Potwierdź nowe hasło -->
          <div class="mb-3">
            <label for="new_password_confirm" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('editProfile.confirmNewPassword') }}
            </label>
            <input
              v-model="newPasswordConfirm"
              type="password"
              id="new_password_confirm"
              :class="[
                'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
                hasFieldError('new_password_confirmation') ? 'border-red-500' : 'border-gray-300'
              ]"
              :placeholder="t('editProfile.confirmNewPasswordPlaceholder')"
            >
            <p v-if="getFieldError('new_password_confirmation')" class="text-xs text-red-600 mt-1">
              {{ getFieldError('new_password_confirmation') }}
            </p>
          </div>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            @click="handleCancel"
            class="py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="loading"
            class="py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('categories.saving') : t('categories.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
